import posthog from "posthog-js";
import i18n from "i18next";
import { axiosBasic, getUserInfo } from "../../../api/axios";
import { config } from "../../../api/endPoints";
import { Common } from "../../../utils";
import {
  addDays,
  formatApprovalQueuePeriodFromDto,
  formatTimeLogWeeklyDayLabel,
  formatWeekRangeReadable,
  parseCivilDateFromApi,
  toUtcDateOnlyISO,
} from "./timesheetDateUtils";
import {
  normalizeTimeSheetApprovalStatus,
  TimeSheetApprovalStatus,
  TimeSheetStatus,
} from "./timesheetConstants";

const { TeamProject, UserTimeSheet, UserTimeSheetApproval } = config.REST_API;

const round2 = (n) => Math.round(n * 100) / 100;

/** Hash string to HSL color (same idea as weekly entry form). */
export const colorFromProjectId = (projectId) => {
  if (!projectId) return "#a8b4c2";
  let h = 0;
  const s = String(projectId);
  for (let i = 0; i < s.length; i++) h = s.charCodeAt(i) + ((h << 5) - h);
  const hue = Math.abs(h) % 360;
  /* Lower saturation / higher lightness for a calmer, report-friendly palette */
  return `hsl(${hue} 26% 58%)`;
};

/**
 * GET TeamProject/timesheetProjects?userId=
 * @param {string} [userId] defaults to current user
 */
/**
 * GET UserTimeSheet/weekly?weekStartDate=&weekEndDate=
 * @param {Date} weekStart
 * @param {Date} [weekEnd] defaults to weekStart + 6 days
 */
export const fetchWeeklyTimeSheet = async (weekStart, weekEnd) => {
  const end = weekEnd ?? addDays(weekStart, 6);
  const params = {
    weekStartDate: toUtcDateOnlyISO(weekStart),
    weekEndDate: toUtcDateOnlyISO(end),
  };
  const res = await axiosBasic.get(UserTimeSheet.Weekly, { params });
  return res.data;
};

/**
 * GET UserTimeSheet/off-days — JS Date.getDay() values 0..6 (Sun..Sat) for non-business days.
 * When every day is a business day, returns an empty array.
 */
export const fetchMyOfficeOffDays = async () => {
  const res = await axiosBasic.get(UserTimeSheet.OffDays);
  const envelope = res.data;
  const d = envelope?.data ?? envelope?.Data ?? envelope;
  const allBiz =
    d?.allDaysAreBusinessDays === true ||
    d?.AllDaysAreBusinessDays === true;
  if (allBiz) return [];
  const raw = d?.offDays ?? d?.OffDays;
  if (!Array.isArray(raw)) return [0, 6];
  const out = raw
    .map((x) => Number(x))
    .filter((n) => Number.isInteger(n) && n >= 0 && n <= 6);
  return out;
};

/**
 * GET UserTimeSheetApproval/Summary?weekRangeStart=&weekRangeEnd=
 * Range may be a single Mon–Sun week or any span (e.g. full calendar month for aggregated stats).
 * @param {Date} weekStart
 * @param {Date} [weekEnd] defaults to weekStart + 6 days
 */
export const fetchApprovalQueueSummary = async (weekStart, weekEnd) => {
  const end = weekEnd ?? addDays(weekStart, 6);
  const params = {
    weekRangeStart: toUtcDateOnlyISO(weekStart),
    weekRangeEnd: toUtcDateOnlyISO(end),
  };
  const res = await axiosBasic.get(UserTimeSheetApproval.Summary, { params });
  return res.data;
};

/** Unwrap ObjectResult / envelope for approval queue summary DTO */
export const mapApprovalQueueSummary = (apiData) => {
  const d = apiData?.data ?? apiData?.Data ?? apiData?.value ?? apiData?.Value ?? apiData;
  if (!d || typeof d !== "object") return null;
  return {
    total: Number(d.total ?? d.Total ?? 0) || 0,
    awaitingApproval: Number(d.awaitingApproval ?? d.AwaitingApproval ?? 0) || 0,
    approved: Number(d.approved ?? d.Approved ?? 0) || 0,
    returned: Number(d.returned ?? d.Returned ?? 0) || 0,
    rejected: Number(d.rejected ?? d.Rejected ?? 0) || 0,
  };
};

/** Unwrap ObjectResult / envelope */
export const unwrapWeeklyPayload = (apiData) => {
  const d = apiData?.data ?? apiData?.Data ?? apiData?.value ?? apiData?.Value ?? apiData;
  return d ?? null;
};

const omitEmptyParams = (obj) => {
  const out = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v === undefined || v === null || v === "") continue;
    out[k] = v;
  }
  return out;
};

/** @param {unknown} raw */
const normalizeAvailableActions = (raw) => {
  if (!Array.isArray(raw)) return [];
  return raw.map((a) => String(a).trim().toLowerCase()).filter(Boolean);
};

const EM_DASH = "—";

/** @param {unknown} item */
const labelFromApprovalQueueProjectItem = (item) => {
  if (item == null) return "";
  if (typeof item === "string") {
    const x = item.trim();
    return x && x !== EM_DASH ? x : "";
  }
  if (typeof item === "object") {
    const pn =
      item.projectName ?? item.ProjectName ?? item.name ?? item.Name ?? item.title ?? item.Title ?? item.label ?? item.Label;
    const team = item.teamName ?? item.TeamName;
    if (pn != null && String(pn).trim()) {
      const p = String(pn).trim();
      const t = team != null && String(team).trim() ? String(team).trim() : "";
      return t ? `${p} — ${t}` : p;
    }
    return "";
  }
  return "";
};

/**
 * Labels for approval-queue project tags (array from API or split string on , ; |).
 * @param {unknown} raw
 * @returns {string[]}
 */
export const normalizeApprovalQueueProjectLabels = (raw) => {
  if (raw == null) return [];
  if (Array.isArray(raw)) {
    return raw.map(labelFromApprovalQueueProjectItem).filter(Boolean);
  }
  const s = String(raw).trim();
  if (!s || s === EM_DASH) return [];
  const parts = s.split(/\s*[,;|]\s+/).map((x) => x.trim()).filter((x) => x && x !== EM_DASH);
  if (parts.length > 0) return parts;
  return s ? [s] : [];
};

/**
 * Queue row from TimeSheetApprovalQueueRowDto
 * @param {unknown} dto
 */
export const mapApprovalQueueRowDto = (dto) => {
  if (!dto || typeof dto !== "object") return null;
  const emp = dto.employee ?? dto.Employee ?? {};
  const name =
    emp.fullName ??
    emp.FullName ??
    emp.name ??
    emp.Name ??
    [emp.firstName ?? emp.FirstName, emp.lastName ?? emp.LastName].filter(Boolean).join(" ") ??
    "";
  const aid = dto.approvalId ?? dto.ApprovalId;
  const tid = dto.timeSheetId ?? dto.TimeSheetId;
  const id = aid != null && aid !== "" ? String(aid) : tid != null ? String(tid) : "";
  if (!id) return null;
  const projectsRaw = dto.projects ?? dto.Projects;
  const projectLabels = normalizeApprovalQueueProjectLabels(projectsRaw);
  const projectsSummary =
    projectLabels.length > 0 ? projectLabels.join(", ") : String(projectsRaw ?? "").trim() || EM_DASH;
  return {
    id,
    approvalId: aid != null ? String(aid) : "",
    timeSheetId: tid != null ? String(tid) : "",
    employeeUserId: String(dto.employeeUserId ?? dto.EmployeeUserId ?? ""),
    employeeName: String(name || "—"),
    designation: String(emp.designation ?? emp.Designation ?? ""),
    email: String(emp.email ?? emp.Email ?? ""),
    photoUrl: String(dto.profileImageUrl ?? dto.ProfileImageUrl ?? "").trim() || undefined,
    department: String(dto.department ?? dto.Department ?? "").trim(),
    office: String(dto.office ?? dto.Office ?? "").trim(),
    projectsSummary,
    projectLabels,
    periodLabel: formatApprovalQueuePeriodFromDto(dto, i18n.language),
    totalHours: Number(dto.hours ?? dto.Hours ?? 0),
    status: (() => {
      const ts = dto.timeSheetStatus ?? dto.TimeSheetStatus;
      if (ts != null && ts !== "") return Number(ts);
      return TimeSheetStatus.Submitted;
    })(),
    approvalStatus:
      normalizeTimeSheetApprovalStatus(
        dto.approvalStatus ??
          dto.ApprovalStatus ??
          dto.timeSheetApprovalStatus ??
          dto.TimeSheetApprovalStatus
      ) ?? TimeSheetApprovalStatus.Pending,
    availableActions: normalizeAvailableActions(dto.availableActions ?? dto.AvailableActions),
  };
};

/** @param {unknown} raw */
export const normalizeApprovalQueuePagedResponse = (raw) => {
  const d = unwrapWeeklyPayload(raw);
  const env = raw && typeof raw === "object" ? raw : null;
  if (!d || typeof d !== "object") {
    return {
      items: [],
      totalCount: 0,
      page: 1,
      take: 20,
      totalPages: 1,
      hasNextPage: false,
      hasPreviousPage: false,
    };
  }
  const itemsRaw = d.items ?? d.Items ?? [];
  const items = Array.isArray(itemsRaw) ? itemsRaw.map(mapApprovalQueueRowDto).filter(Boolean) : [];
  const totalCount = Number(
    d.totalCount ??
      d.TotalCount ??
      d.total ??
      d.Total ??
      d.recordsTotal ??
      d.RecordsTotal ??
      env?.totalCount ??
      env?.TotalCount ??
      0
  );
  const page = Number(d.page ?? d.Page ?? env?.page ?? env?.Page ?? 1) || 1;
  const take = Number(d.take ?? d.Take ?? env?.take ?? env?.Take ?? 20) || 20;
  const hasNextPage = Boolean(d.hasNextPage ?? d.HasNextPage ?? env?.hasNextPage ?? env?.HasNextPage);
  const hasPreviousPage = Boolean(d.hasPreviousPage ?? d.HasPreviousPage ?? env?.hasPreviousPage ?? env?.HasPreviousPage);

  let totalPages = Number(
    d.totalPages ?? d.TotalPages ?? d.pageCount ?? d.PageCount ?? env?.totalPages ?? env?.TotalPages
  );
  if (!Number.isFinite(totalPages) || totalPages < 1) {
    totalPages = 1;
  }

  /** Prefer totalCount when API under-reports or omits totalPages (common with paged envelopes). */
  const pagesFromCount = totalCount > 0 && take > 0 ? Math.max(1, Math.ceil(totalCount / take)) : 0;
  if (pagesFromCount > totalPages) {
    totalPages = pagesFromCount;
  }

  if (hasNextPage && items.length >= take) {
    totalPages = Math.max(totalPages, page + 1);
  }

  return {
    items,
    totalCount,
    page,
    take,
    totalPages,
    hasNextPage,
    hasPreviousPage,
  };
};

/**
 * GET UserTimeSheetApproval/Queue — paginated; filters match backend query names.
 * @param {Record<string, unknown>} params
 */
export const fetchApprovalQueue = async (params) => {
  const res = await axiosBasic.get(UserTimeSheetApproval.Queue, {
    params: omitEmptyParams(params),
  });
  assertTimesheetResponseOk(res.data);
  return normalizeApprovalQueuePagedResponse(res.data);
};

/**
 * Group UserTimeSheetEntries by calendar day for approval detail modal.
 * @param {unknown} raw UserTimeSheets envelope
 */
export const normalizeApprovalTimesheetDetail = (raw) => {
  const d = unwrapWeeklyPayload(raw);
  if (!d || typeof d !== "object") return null;
  const entries = d.userTimeSheetEntries ?? d.UserTimeSheetEntries ?? [];
  if (!Array.isArray(entries)) return null;

  const byDay = new Map();
  for (const e of entries) {
    const wdRaw = e.workDate ?? e.WorkDate;
    if (wdRaw == null) continue;
    const wd = parseCivilDateFromApi(wdRaw);
    if (!wd) continue;
    const key = `${wd.getFullYear()}-${String(wd.getMonth() + 1).padStart(2, "0")}-${String(wd.getDate()).padStart(2, "0")}`;
    let hours = e.hoursWorked ?? e.HoursWorked;
    if (hours == null || Number.isNaN(Number(hours))) {
      const mins = e.minutesWorked ?? e.MinutesWorked;
      if (typeof mins === "number" && !Number.isNaN(mins)) hours = mins / 60;
    }
    const h = typeof hours === "number" && !Number.isNaN(hours) ? round2(hours) : 0;
    const projectTitle =
      e.projectTitle ??
      e.ProjectTitle ??
      e.projectName ??
      e.ProjectName ??
      e.taskDescription ??
      e.TaskDescription ??
      "—";
    if (!byDay.has(key)) byDay.set(key, { sort: wd.getTime(), dayLabel: formatTimeLogWeeklyDayLabel(wd), entries: [] });
    byDay.get(key).entries.push({ projectName: String(projectTitle), hours: h });
  }

  const detailDays = Array.from(byDay.values())
    .sort((a, b) => a.sort - b.sort)
    .map(({ dayLabel, entries }) => ({ dayLabel, entries }));

  const ws = parseCivilDateFromApi(d.weekStartDate ?? d.WeekStartDate);
  const we = parseCivilDateFromApi(d.weekEndDate ?? d.WeekEndDate);
  const periodLabel =
    ws && we && !Number.isNaN(ws.getTime()) && !Number.isNaN(we.getTime())
      ? formatWeekRangeReadable(ws, we, i18n.language)
      : "";

  const totalH = d.totalHours ?? d.TotalHours;
  let totalHours = typeof totalH === "number" && !Number.isNaN(totalH) ? totalH : null;
  if (totalHours == null) {
    const mins = d.totalMinutes ?? d.TotalMinutes;
    if (typeof mins === "number" && !Number.isNaN(mins)) totalHours = round2(mins / 60);
    else totalHours = round2(detailDays.reduce((a, day) => a + day.entries.reduce((b, x) => b + x.hours, 0), 0));
  }

  return {
    timeSheetId: String(d.id ?? d.Id ?? ""),
    periodLabel,
    status: Number(d.status ?? d.Status ?? 0),
    approvalStatus: normalizeTimeSheetApprovalStatus(d.approvalStatus ?? d.ApprovalStatus),
    totalHours,
    detailDays,
  };
};

/**
 * GET UserTimeSheetApproval/Detail/{timeSheetId}
 * @param {string} timeSheetId
 */
export const fetchApprovalTimesheetDetail = async (timeSheetId) => {
  const id = encodeURIComponent(String(timeSheetId));
  const res = await axiosBasic.get(`${UserTimeSheetApproval.Detail}/${id}`);
  assertTimesheetResponseOk(res.data);
  return normalizeApprovalTimesheetDetail(res.data);
};

/**
 * POST UserTimeSheetApproval/Decision
 * @param {{ approvalId: string, action: string, remarks?: string }} body action: approve | reject | return
 */
export const postApprovalDecision = async (body) => {
  const res = await axiosBasic.post(UserTimeSheetApproval.Decision, {
    approvalId: String(body.approvalId),
    action: String(body.action).toLowerCase(),
    remarks: body.remarks != null ? String(body.remarks) : "",
  });
  assertTimesheetResponseOk(res.data);
  return res.data;
};

/**
 * GET UserTimeSheetApproval export endpoints — same query as Queue (no pagination).
 * @param {string} path
 * @param {Record<string, unknown>} query
 */
const fetchApprovalQueueExportBlob = async (path, query) => {
  const res = await axiosBasic.get(path, {
    params: omitEmptyParams(query),
    responseType: "blob",
  });
  const blob = res.data;
  if (blob instanceof Blob && blob.type && blob.type.includes("application/json")) {
    const text = await blob.text();
    try {
      const json = JSON.parse(text);
      assertTimesheetResponseOk(json);
    } catch (e) {
      if (e?.isTimesheetApiEnvelopeError) throw e;
      throw new Error(text || "Export failed");
    }
  }
  return blob;
};

/** @param {Record<string, unknown>} query */
export const fetchApprovalQueueExportExcel = (query) =>
  fetchApprovalQueueExportBlob(UserTimeSheetApproval.ExportExcel, query);

/** @param {Record<string, unknown>} query */
export const fetchApprovalQueueExportCsv = (query) =>
  fetchApprovalQueueExportBlob(UserTimeSheetApproval.ExportCsv, query);

/** @param {Record<string, unknown>} query — landscape PDF of queue (same filters as GetQueue). */
export const fetchApprovalQueueExportPdf = (query) =>
  fetchApprovalQueueExportBlob(UserTimeSheetApproval.ExportPdf, query);

/**
 * GET UserTimeSheetApproval/Pdf/{timeSheetId}
 * @param {string} timeSheetId
 */
export const fetchApprovalTimesheetPdf = async (timeSheetId) => {
  const id = encodeURIComponent(String(timeSheetId));
  const res = await axiosBasic.get(`${UserTimeSheetApproval.Pdf}/${id}`, { responseType: "blob" });
  const blob = res.data;
  if (blob instanceof Blob && blob.type && blob.type.includes("application/json")) {
    const text = await blob.text();
    try {
      const json = JSON.parse(text);
      assertTimesheetResponseOk(json);
    } catch (e) {
      if (e?.isTimesheetApiEnvelopeError) throw e;
      throw new Error(text || "PDF download failed");
    }
  }
  return blob;
};

/**
 * Query object for UserTimeSheetApproval Queue / ExportExcel / ExportCsv / ExportPdf.
 * @param {object} o
 * @param {"monthly"|"weekly"} o.approvalsViewMode UI view key
 * @param {Date} o.monthStart
 * @param {Date} o.effectiveWeekStart
 * @param {Date} o.weekEnd
 * @param {{ project: string, status: string }} o.filters — `status` is {@link TimeSheetApprovalStatus} as string or `"all"`
 * @param {string} o.departmentFilter "all" | id string
 * @param {string} o.officeFilter "all" | id string
 * @param {string} o.employeeSearch trimmed
 * @param {number} o.page
 * @param {number} o.take
 */
export const buildApprovalQueueQuery = ({
  approvalsViewMode,
  monthStart,
  effectiveWeekStart,
  weekEnd,
  filters,
  departmentFilter,
  officeFilter,
  employeeSearch,
  page,
  take,
}) => {
  const base = {
    page,
    take,
    employeeSearch: employeeSearch || undefined,
    departmentId: (() => {
      if (departmentFilter === "all" || departmentFilter === "") return undefined;
      const n = Number(departmentFilter);
      return Number.isFinite(n) ? n : undefined;
    })(),
    officeId: (() => {
      if (officeFilter === "all" || officeFilter === "") return undefined;
      const n = Number(officeFilter);
      return Number.isFinite(n) ? n : undefined;
    })(),
    projectId: filters.project !== "all" ? String(filters.project) : undefined,
    /* "Any status": backend queue often defaults to pending only unless this flag is set */
    ...(String(filters.status) === "all" || filters.status === ""
      ? { showAllApprovalStatuses: true }
      : (() => {
          const n = Number(filters.status);
          return Number.isFinite(n) ? { approvalStatus: n } : { showAllApprovalStatuses: true };
        })()),
  };
  if (approvalsViewMode === "monthly") {
    return {
      ...base,
      viewMode: TIME_LOG_VIEW_MODE.Monthly,
      year: monthStart.getFullYear(),
      month: monthStart.getMonth() + 1,
    };
  }
  return {
    ...base,
    viewMode: TIME_LOG_VIEW_MODE.Weekly,
    weekRangeStart: toUtcDateOnlyISO(effectiveWeekStart),
    weekRangeEnd: toUtcDateOnlyISO(weekEnd),
  };
};

/** Same filters as {@link buildApprovalQueueQuery} without pagination (for queue exports). */
export const buildApprovalQueueExportQuery = (args) => {
  const q = { ...omitEmptyParams(buildApprovalQueueQuery({ ...args, page: 1, take: 1 })) };
  delete q.page;
  delete q.take;
  return q;
};

/** Team project row from WeeklyTimeSheetViewDto.Projects */
export const normalizeTeamProjectAssignment = (raw) => {
  if (!raw || typeof raw !== "object") return null;
  const id = raw.projectId ?? raw.ProjectId;
  const name = raw.projectName ?? raw.ProjectName ?? "";
  const teamName = raw.teamName ?? raw.TeamName ?? "";
  if (id == null || id === "") return null;
  return { id: String(id), name: String(name), teamName: String(teamName || "") };
};

export const mapWeeklyProjects = (weeklyDto) => {
  const d = unwrapWeeklyPayload(weeklyDto);
  const list = d?.projects ?? d?.Projects;
  if (!Array.isArray(list)) return [];
  return list.map(normalizeTeamProjectAssignment).filter(Boolean);
};

/** Resolve status from weekly DTO */
export const getTimeSheetStatusFromWeekly = (weeklyDto) => {
  const d = unwrapWeeklyPayload(weeklyDto);
  if (!d) return null;
  const s = d.timeSheetStatus ?? d.TimeSheetStatus;
  if (s != null && s !== "") return Number(s);
  const ts = d.timeSheet ?? d.TimeSheet;
  const st = ts?.status ?? ts?.Status;
  return st != null && st !== "" ? Number(st) : null;
};

export const getTimeSheetFromWeekly = (weeklyDto) => {
  const d = unwrapWeeklyPayload(weeklyDto);
  if (!d) return null;
  return d.timeSheet ?? d.TimeSheet ?? null;
};

export const getTimeSheetIdFromWeekly = (weeklyDto) => {
  const ts = getTimeSheetFromWeekly(weeklyDto);
  if (!ts) return null;
  return ts.id ?? ts.Id ?? null;
};

/** Approver workflow status from WeeklyTimeSheetViewDto (null if no active approval rows). */
export const getTimeSheetApprovalStatusFromWeekly = (weeklyDto) => {
  const d = unwrapWeeklyPayload(weeklyDto);
  if (!d) return null;
  const raw = d.timeSheetApprovalStatus ?? d.TimeSheetApprovalStatus;
  return normalizeTimeSheetApprovalStatus(raw);
};

/**
 * POST UserTimeSheet/UpdateTimeSheetStatus
 * Body (camelCase): align with C# `UpdateTimeSheetStatusRequest` (e.g. `timeSheetId` + `action` / `Action`).
 */
export const updateTimeSheetStatus = async (requestBody) => {
  const res = await axiosBasic.post(UserTimeSheet.UpdateTimeSheetStatus, requestBody);
  assertTimesheetResponseOk(res.data);
  return res.data;
};

/**
 * Unsubmit: Submitted → Draft; backend deactivates approval rows.
 * C# `TimeSheetStatusUpdateAction.Unsubmit = 1`
 */
export const unsubmitTimeSheet = (timeSheetId) =>
  updateTimeSheetStatus({ timeSheetId: String(timeSheetId), action: 1 });

/** Day index 0..6 for entry workDate within the week of dayDates */
export const dayIndexInWeek = (workDate, dayDates) => {
  const x = new Date(workDate);
  if (Number.isNaN(x.getTime())) return -1;
  const y = x.getFullYear();
  const m = x.getMonth();
  const day = x.getDate();
  for (let i = 0; i < dayDates.length; i++) {
    const dd = dayDates[i];
    if (dd.getFullYear() === y && dd.getMonth() === m && dd.getDate() === day) return i;
  }
  return -1;
};

/**
 * Build grid rows from API entries (grouped by project).
 * @param {unknown[]} entries
 * @param {Date[]} dayDates
 * @param {Map<string, { id: string, name: string, teamName: string }>} projectLookup
 */
export const buildRowsFromTimeSheetEntries = (entries, dayDates, projectLookup) => {
  if (!Array.isArray(entries) || !entries.length) return [];
  const byProject = new Map();
  for (const e of entries) {
    const pid = String(e.projectId ?? e.ProjectId ?? "");
    if (!pid) continue;
    if (!byProject.has(pid)) {
      const meta = projectLookup.get(pid) || {};
      byProject.set(pid, {
        id: `r-${pid}-${Date.now()}`,
        projectId: pid,
        projectName: meta.name || String(e.projectTitle ?? e.ProjectTitle ?? ""),
        teamName: meta.teamName || "",
        detail: "",
        isBillable: e.isBillable ?? e.IsBillable ?? true,
        color: colorFromProjectId(pid),
        hours: [null, null, null, null, null, null, null],
      });
    }
    const row = byProject.get(pid);
    const di = dayIndexInWeek(e.workDate ?? e.WorkDate, dayDates);
    if (di < 0) continue;
    let hours = e.hoursWorked ?? e.HoursWorked;
    if (hours == null || Number.isNaN(Number(hours))) {
      const mins = e.minutesWorked ?? e.MinutesWorked;
      if (typeof mins === "number" && !Number.isNaN(mins)) hours = mins / 60;
    }
    const h = typeof hours === "number" && !Number.isNaN(hours) ? round2(hours) : null;
    if (h == null || h <= 0) continue;
    const prev = row.hours[di];
    row.hours[di] = prev == null ? h : round2((prev || 0) + h);
    const td = String(e.taskDescription ?? e.TaskDescription ?? "").trim();
    if (td) {
      if (!row.detail) row.detail = td;
      else if (!row.detail.includes(td)) row.detail = `${row.detail}; ${td}`;
    }
    const bill = e.isBillable ?? e.IsBillable;
    if (typeof bill === "boolean") row.isBillable = bill;
  }
  return Array.from(byProject.values());
};

/** Resolve current user id for TeamProject/timesheetProjects?userId= */
export const getTimesheetProjectsUserId = (explicitUserId) => {
  if (explicitUserId != null && explicitUserId !== "") return String(explicitUserId);
  const info = getUserInfo();
  if (!info || typeof info !== "object") return null;
  const uid =
    info.selfUserId ??
    info.SelfUserId ??
    info.userId ??
    info.UserId ??
    info.id ??
    info.Id ??
    null;
  return uid != null && uid !== "" ? String(uid) : null;
};

export const fetchTimesheetProjects = async (userId) => {
  const uid = getTimesheetProjectsUserId(userId);
  const params = uid ? { userId: uid } : {};
  const res = await axiosBasic.get(TeamProject.TimesheetProjects, { params });
  return res.data;
};

/**
 * TeamProject/timesheetProjects may return the list in various envelopes (direct array, data[], Data.Projects, $values, etc.).
 * @param {unknown} apiData
 * @returns {unknown[]}
 */
export const extractTimesheetProjectsList = (apiData) => {
  const asArray = (x) => (Array.isArray(x) ? x : null);

  if (apiData == null) return [];
  const top = asArray(apiData);
  if (top) return top;

  const unwrap =
    apiData?.data ??
    apiData?.Data ??
    apiData?.value ??
    apiData?.Value ??
    apiData?.result ??
    apiData?.Result;

  let cur = unwrap;
  if (asArray(cur)) return cur;
  if (cur != null && typeof cur === "object") {
    const nested =
      asArray(cur.projects) ??
      asArray(cur.Projects) ??
      asArray(cur.teamProjects) ??
      asArray(cur.TeamProjects) ??
      asArray(cur.items) ??
      asArray(cur.Items) ??
      asArray(cur.records) ??
      asArray(cur.Records) ??
      asArray(cur.$values);
    if (nested) return nested;
  }

  if (apiData != null && typeof apiData === "object") {
    const flat =
      asArray(apiData.projects) ??
      asArray(apiData.Projects) ??
      asArray(apiData.teamProjects) ??
      asArray(apiData.TeamProjects);
    if (flat) return flat;
  }

  return [];
};

/**
 * Backend may return HTTP 200 with `success: false` / `Success: false` in the body (ObjectResult pattern).
 * Throws an error shaped like an axios error so {@link handleTimesheetApiError} can show the server message.
 */
export const assertTimesheetResponseOk = (body) => {
  if (body == null || typeof body !== "object") return;
  const failed = body.success === false || body.Success === false;
  if (!failed) return;
  const msg = body.message ?? body.Message ?? "Request failed";
  const err = new Error(msg);
  err.response = { data: { message: msg, Message: msg } };
  err.isTimesheetApiEnvelopeError = true;
  throw err;
};

/**
 * POST UserTimeSheet/LogTimeSheet
 * Body: { weekStartDate, weekEndDate?, entries: [{ projectId, workDate, hoursWorked, taskDescription?, isBillable }] }
 */
export const logTimeSheet = async (requestBody) => {
  const res = await axiosBasic.post(UserTimeSheet.LogTimeSheet, requestBody);
  assertTimesheetResponseOk(res.data);
  return res.data;
};

/**
 * POST UserTimeSheet/SubmitTimeSheet — after draft exists
 * Body: { timeSheetId }
 */
export const submitTimeSheetForApproval = async (timeSheetId) => {
  const res = await axiosBasic.post(UserTimeSheet.SubmitTimeSheet, { timeSheetId });
  assertTimesheetResponseOk(res.data);
  return res.data;
};

/** Normalize API project row to { id, name, teamName } */
export const normalizeTimesheetProject = (raw) => {
  if (!raw || typeof raw !== "object") return null;
  const nested = raw.project ?? raw.Project;
  const id =
    raw.id ??
    raw.Id ??
    raw.projectId ??
    raw.ProjectId ??
    (nested && (nested.id ?? nested.Id ?? nested.projectId ?? nested.ProjectId)) ??
    null;
  const name =
    raw.name ??
    raw.Name ??
    raw.title ??
    raw.Title ??
    raw.projectTitle ??
    raw.ProjectTitle ??
    raw.projectName ??
    raw.ProjectName ??
    (nested && (nested.name ?? nested.Name ?? nested.title ?? nested.Title)) ??
    "";
  const teamName =
    raw.teamName ??
    raw.TeamName ??
    raw.team?.name ??
    raw.Team?.Name ??
    raw.team ??
    "";
  if (id == null || id === "") return null;
  return { id: String(id), name: String(name || "").trim() || `Project ${id}`, teamName: String(teamName || "") };
};

export const mapTimesheetProjectsResponse = (apiData) => {
  const list = extractTimesheetProjectsList(apiData);
  return list.map((row) => normalizeTimesheetProject(row)).filter(Boolean);
};

/** Extract timesheet id from LogTimeSheet success payload */
export const getTimeSheetIdFromLogResponse = (data) => {
  const d = data?.data ?? data?.Data ?? data?.value ?? data?.Value ?? data;
  if (d == null) return null;
  if (typeof d === "string") return d;
  return d.id ?? d.Id ?? d.timeSheetId ?? d.TimeSheetId ?? null;
};

/** Matches backend TimeLogViewMode */
export const TIME_LOG_VIEW_MODE = { Monthly: 1, Weekly: 2 };

/** @param {unknown} raw */
export const normalizeProjectTitles = (raw) => {
  if (!Array.isArray(raw)) return [];
  return raw.map((x) => String(x ?? "").trim()).filter(Boolean);
};

export const mapTimeLogMonthlyRowDto = (dto) => {
  if (!dto || typeof dto !== "object") return null;
  const tsid = dto.timeSheetId ?? dto.TimeSheetId;
  const ws = dto.weekStartDate ?? dto.WeekStartDate;
  const we = dto.weekEndDate ?? dto.WeekEndDate;
  const weekStart = ws ? parseCivilDateFromApi(ws) : null;
  const weekEnd = we ? parseCivilDateFromApi(we) : null;
  const apiLabel = dto.weekRangeLabel ?? dto.WeekRangeLabel ?? "";
  const weekLabel =
    weekStart &&
    weekEnd &&
    !Number.isNaN(weekStart.getTime()) &&
    !Number.isNaN(weekEnd.getTime())
      ? formatWeekRangeReadable(weekStart, weekEnd)
      : apiLabel;
  return {
    id: String(tsid || (weekStart ? weekStart.toISOString() : Math.random())),
    timeSheetId: tsid != null ? String(tsid) : "",
    weekLabel,
    weekStart,
    weekEnd,
    weeklyHours: Number(dto.weeklyHours ?? dto.WeeklyHours ?? 0),
    status: Number(dto.status ?? dto.Status ?? 0),
    projectTitles: normalizeProjectTitles(dto.projectTitles ?? dto.ProjectTitles),
  };
};

export const mapTimeLogWeeklyDayRowDto = (dto, idx) => {
  if (!dto || typeof dto !== "object") return null;
  const wd = dto.workDate ?? dto.WorkDate;
  const projects = dto.projects ?? dto.Projects ?? [];
  const parts = projects.map((p) => {
    const title = p.projectTitle ?? p.ProjectTitle ?? "";
    const h = p.hours ?? p.Hours ?? 0;
    return title ? `${title} (${Number(h).toFixed(1)}h)` : "";
  });
  const workDate = wd ? new Date(wd) : null;
  const apiDayLabel = dto.dayLabel ?? dto.DayLabel ?? "";
  const dayLabel =
    workDate && !Number.isNaN(workDate.getTime())
      ? formatTimeLogWeeklyDayLabel(workDate) || apiDayLabel
      : apiDayLabel;
  return {
    id: `wday-${idx}-${workDate && !Number.isNaN(workDate.getTime()) ? workDate.getTime() : idx}`,
    workDate,
    dayLabel,
    dailyHours: Number(dto.dailyHours ?? dto.DailyHours ?? 0),
    status: Number(dto.status ?? dto.Status ?? 0),
    timeSheetId: dto.timeSheetId ?? dto.TimeSheetId ?? "",
    projects,
    projectSummary: parts.filter(Boolean).join(" · ") || "—",
  };
};

export const normalizeTimeLogPagedResponse = (raw) => {
  if (!raw || typeof raw !== "object") {
    return {
      viewMode: TIME_LOG_VIEW_MODE.Monthly,
      page: 1,
      pageSize: 20,
      totalCount: 0,
      monthlyRows: [],
      weeklyRows: [],
    };
  }
  const monthly = raw.monthlyRows ?? raw.MonthlyRows;
  const weekly = raw.weeklyRows ?? raw.WeeklyRows;
  return {
    viewMode: Number(raw.viewMode ?? raw.ViewMode ?? TIME_LOG_VIEW_MODE.Monthly),
    page: Number(raw.page ?? raw.Page ?? 1),
    pageSize: Number(raw.pageSize ?? raw.PageSize ?? 20),
    totalCount: Number(raw.totalCount ?? raw.TotalCount ?? 0),
    year: raw.year ?? raw.Year,
    month: raw.month ?? raw.Month,
    monthRangeStart: raw.monthRangeStart ?? raw.MonthRangeStart,
    monthRangeEnd: raw.monthRangeEnd ?? raw.MonthRangeEnd,
    weekStartDate: raw.weekStartDate ?? raw.WeekStartDate,
    weekEndDate: raw.weekEndDate ?? raw.WeekEndDate,
    monthlyRows: Array.isArray(monthly) ? monthly.map(mapTimeLogMonthlyRowDto).filter(Boolean) : [],
    weeklyRows: Array.isArray(weekly)
      ? weekly.map((row, i) => mapTimeLogWeeklyDayRowDto(row, i)).filter(Boolean)
      : [],
  };
};

export const normalizeTimesheetDetailDto = (raw) => {
  if (!raw || typeof raw !== "object") return null;
  const daysRaw = raw.days ?? raw.Days ?? [];
  const days = daysRaw.map((day) => {
    const projects = day.projects ?? day.Projects ?? [];
    const wdRaw = day.workDate ?? day.WorkDate;
    const wd = wdRaw ? new Date(wdRaw) : null;
    const apiHeading = day.dayHeading ?? day.DayHeading ?? "";
    const apiDayLabel = day.dayLabel ?? day.DayLabel ?? "";
    const dayHeading =
      wd && !Number.isNaN(wd.getTime()) ? formatTimeLogWeeklyDayLabel(wd) || apiHeading : apiHeading;
    return {
      workDate: wdRaw,
      dayHeading,
      dayLabel: apiDayLabel,
      dailyHours: Number(day.dailyHours ?? day.DailyHours ?? 0),
      status: Number(day.status ?? day.Status ?? 0),
      projects: projects.map((p) => ({
        projectId: p.projectId ?? p.ProjectId,
        projectTitle: p.projectTitle ?? p.ProjectTitle ?? "",
        hours: Number(p.hours ?? p.Hours ?? 0),
      })),
    };
  });
  return {
    timeSheetId: String(raw.timeSheetId ?? raw.TimeSheetId ?? ""),
    weekStartDate: raw.weekStartDate ?? raw.WeekStartDate,
    weekEndDate: raw.weekEndDate ?? raw.WeekEndDate,
    weekRangeLabel: raw.weekRangeLabel ?? raw.WeekRangeLabel ?? "",
    weekTitle: raw.weekTitle ?? raw.WeekTitle ?? "",
    status: Number(raw.status ?? raw.Status ?? 0),
    totalWeeklyHours: Number(raw.totalWeeklyHours ?? raw.TotalWeeklyHours ?? 0),
    submittedDate: raw.submittedDate ?? raw.SubmittedDate,
    days,
  };
};

/**
 * GET UserTimeSheet/getTimeLog — paginated monthly week rows or weekly day rows.
 * @param {Record<string, unknown>} params viewMode, year/month OR weekStartDate/weekEndDate, page, pageSize, status?, projectId?
 */
export const fetchTimeLog = async (params) => {
  const res = await axiosBasic.get(UserTimeSheet.GetTimeLog, { params });
  assertTimesheetResponseOk(res.data);
  return normalizeTimeLogPagedResponse(unwrapWeeklyPayload(res.data));
};

/**
 * GET UserTimeSheet/timesheet-detail — modal detail for a monthly row.
 * @param {{ timeSheetId?: string, weekStartDate?: string, weekEndDate?: string }} params
 */
export const fetchTimesheetDetail = async (params) => {
  const res = await axiosBasic.get(UserTimeSheet.TimesheetDetail, { params });
  assertTimesheetResponseOk(res.data);
  return normalizeTimesheetDetailDto(unwrapWeeklyPayload(res.data));
};

/**
 * GET UserTimeSheet/ExportTimeLog — full export (not paginated).
 * @param {string} format xlsx | pdf | csv
 * @param {Record<string, unknown>} query same filters as getTimeLog (no page)
 */
export const fetchTimeLogExportBlob = async (format, query) => {
  const res = await axiosBasic.get(UserTimeSheet.ExportTimeLog, {
    params: { ...query, format },
    responseType: "blob",
  });
  const blob = res.data;
  if (blob instanceof Blob && blob.type && blob.type.includes("application/json")) {
    const text = await blob.text();
    try {
      const json = JSON.parse(text);
      assertTimesheetResponseOk(json);
    } catch (e) {
      if (e?.isTimesheetApiEnvelopeError) throw e;
      throw new Error(text || "Export failed");
    }
  }
  return blob;
};

export const triggerBlobDownload = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

/**
 * Shared period query for UserTimeSheet report endpoints (periodStart/periodEnd or year+month).
 * @param {{ periodStart?: Date, periodEnd?: Date, year?: number, month?: number }} p
 */
export const buildUserTimeSheetReportPeriodParams = (p) => {
  if (p?.periodStart && p?.periodEnd) {
    return omitEmptyParams({
      periodStart: toUtcDateOnlyISO(p.periodStart),
      periodEnd: toUtcDateOnlyISO(p.periodEnd),
    });
  }
  if (p?.year != null && p?.month != null) {
    const y = Number(p.year);
    const m = Number(p.month);
    if (Number.isFinite(y) && Number.isFinite(m)) {
      return omitEmptyParams({ year: y, month: m });
    }
  }
  return {};
};

/** @param {unknown} raw */
export const normalizeTeamApprovalVelocityDto = (raw) => {
  const d = unwrapWeeklyPayload(raw);
  if (!d || typeof d !== "object") return null;
  return {
    periodStart: d.periodStart ?? d.PeriodStart ?? null,
    periodEnd: d.periodEnd ?? d.PeriodEnd ?? null,
    pendingCount: Number(d.pendingCount ?? d.PendingCount ?? 0) || 0,
    approvedCount: Number(d.approvedCount ?? d.ApprovedCount ?? 0) || 0,
    rejectedCount: Number(d.rejectedCount ?? d.RejectedCount ?? 0) || 0,
    returnedCount: Number(d.returnedCount ?? d.ReturnedCount ?? 0) || 0,
    totalApprovalRecords: Number(d.totalApprovalRecords ?? d.TotalApprovalRecords ?? 0) || 0,
    approvalVelocityPercent: Number(d.approvalVelocityPercent ?? d.ApprovalVelocityPercent ?? 0) || 0,
  };
};

/** @param {unknown} raw */
export const normalizeTimeSheetAdminDashboard = (raw) => {
  const d = unwrapWeeklyPayload(raw);
  if (!d || typeof d !== "object") return null;
  const prog = d.progress ?? d.Progress ?? {};
  const slicesRaw = prog.slices ?? prog.Slices ?? [];
  const slices = Array.isArray(slicesRaw)
    ? slicesRaw.map((s) => ({
        key: String(s.key ?? s.Key ?? ""),
        label: String(s.label ?? s.Label ?? ""),
        hours: Number(s.hours ?? s.Hours ?? 0) || 0,
        percentOfTotal: Number(s.percentOfTotal ?? s.PercentOfTotal ?? 0) || 0,
      }))
    : [];
  const subRaw = d.submissionStatus ?? d.SubmissionStatus ?? [];
  const submissionStatus = Array.isArray(subRaw)
    ? subRaw.map((s) => ({
        status: String(s.status ?? s.Status ?? ""),
        count: Number(s.count ?? s.Count ?? 0) || 0,
      }))
    : [];
  const hpRaw = d.hoursByProject ?? d.HoursByProject ?? [];
  const hoursByProject = Array.isArray(hpRaw)
    ? hpRaw.map((h) => ({
        projectId: String(h.projectId ?? h.ProjectId ?? ""),
        projectTitle: String(h.projectTitle ?? h.ProjectTitle ?? "—"),
        isInternal: Boolean(h.isInternal ?? h.IsInternal),
        hours: Number(h.hours ?? h.Hours ?? 0) || 0,
        percentOfTotal: Number(h.percentOfTotal ?? h.PercentOfTotal ?? 0) || 0,
      }))
    : [];
  const tot = d.totals ?? d.Totals ?? {};
  const totals = {
    totalHours: Number(tot.totalHours ?? tot.TotalHours ?? 0) || 0,
    billableRatioPercent: Number(tot.billableRatioPercent ?? tot.BillableRatioPercent ?? 0) || 0,
  };
  const emRaw = d.employeePerformanceMatrix ?? d.EmployeePerformanceMatrix ?? [];
  const employeePerformanceMatrix = Array.isArray(emRaw)
    ? emRaw.map((r) => ({
        userId: String(r.userId ?? r.UserId ?? ""),
        employeeName: String(r.employeeName ?? r.EmployeeName ?? "—"),
        email: String(r.email ?? r.Email ?? ""),
        department: String(r.department ?? r.Department ?? ""),
        totalHours: Number(r.totalHours ?? r.TotalHours ?? 0) || 0,
        billableHours: Number(r.billableHours ?? r.BillableHours ?? 0) || 0,
        internalHours: Number(r.internalHours ?? r.InternalHours ?? 0) || 0,
        billableRatioPercent: Number(r.billableRatioPercent ?? r.BillableRatioPercent ?? 0) || 0,
        timesheetCount: Number(r.timesheetCount ?? r.TimesheetCount ?? 0) || 0,
      }))
    : [];
  const utRaw = d.utilizationThread ?? d.UtilizationThread ?? [];
  const utilizationThread = Array.isArray(utRaw)
    ? utRaw.map((u) => ({
        periodStart: u.periodStart ?? u.PeriodStart ?? null,
        periodEnd: u.periodEnd ?? u.PeriodEnd ?? null,
        label: String(u.label ?? u.Label ?? ""),
        totalHours: Number(u.totalHours ?? u.TotalHours ?? 0) || 0,
        billableHours: Number(u.billableHours ?? u.BillableHours ?? 0) || 0,
        utilizationPercent: Number(u.utilizationPercent ?? u.UtilizationPercent ?? 0) || 0,
      }))
    : [];
  return {
    periodStart: d.periodStart ?? d.PeriodStart ?? null,
    periodEnd: d.periodEnd ?? d.PeriodEnd ?? null,
    progress: { slices },
    submissionStatus,
    hoursByProject,
    totals,
    employeePerformanceMatrix,
    utilizationThread,
  };
};

/**
 * GET UserTimeSheet/ApprovalVelocity — managers & admins.
 * @param {{ periodStart?: Date, periodEnd?: Date, year?: number, month?: number }} period
 */
export const fetchApprovalVelocity = async (period) => {
  const params = buildUserTimeSheetReportPeriodParams(period);
  const res = await axiosBasic.get(UserTimeSheet.ApprovalVelocity, { params: omitEmptyParams(params) });
  assertTimesheetResponseOk(res.data);
  return normalizeTeamApprovalVelocityDto(res.data);
};

/**
 * GET UserTimeSheet/TimeSheetsStats — company admins only.
 * @param {{ periodStart?: Date, periodEnd?: Date, year?: number, month?: number }} period
 */
export const fetchTimeSheetsStats = async (period) => {
  const params = buildUserTimeSheetReportPeriodParams(period);
  const res = await axiosBasic.get(UserTimeSheet.TimeSheetsStats, { params: omitEmptyParams(params) });
  assertTimesheetResponseOk(res.data);
  return normalizeTimeSheetAdminDashboard(res.data);
};

const fetchMatrixBlobWithJsonErrorHandling = async (url, params) => {
  const res = await axiosBasic.get(url, {
    params: omitEmptyParams(params),
    responseType: "blob",
  });
  const blob = res.data;
  if (blob instanceof Blob && blob.type && blob.type.includes("application/json")) {
    const text = await blob.text();
    try {
      const json = JSON.parse(text);
      assertTimesheetResponseOk(json);
    } catch (e) {
      if (e?.isTimesheetApiEnvelopeError) throw e;
      throw new Error(text || "Export failed");
    }
  }
  return blob;
};

/**
 * GET UserTimeSheet/GetEmployeePerformanceMatrixExport — admins; format: csv | xlsx
 * @param {string} format
 * @param {{ periodStart?: Date, periodEnd?: Date, year?: number, month?: number }} period
 */
export const fetchEmployeePerformanceMatrixExport = async (format, period) => {
  const params = {
    format: String(format || "xlsx").toLowerCase(),
    ...buildUserTimeSheetReportPeriodParams(period),
  };
  return fetchMatrixBlobWithJsonErrorHandling(UserTimeSheet.GetEmployeePerformanceMatrixExport, params);
};

/**
 * GET UserTimeSheet/GetEmployeePerformanceMatrixPdf — admins; same period rules as matrix export.
 * @param {{ periodStart?: Date, periodEnd?: Date, year?: number, month?: number }} period
 */
export const fetchEmployeePerformanceMatrixPdf = async (period) => {
  const params = buildUserTimeSheetReportPeriodParams(period);
  return fetchMatrixBlobWithJsonErrorHandling(UserTimeSheet.GetEmployeePerformanceMatrixPdf, params);
};

/** @param {unknown} raw */
export const normalizeEmployeePerformanceListPaged = (raw) => {
  const d = unwrapWeeklyPayload(raw);
  if (!d || typeof d !== "object") {
    return {
      items: [],
      totalCount: 0,
      page: 1,
      take: 20,
      totalPages: 0,
      hasNextPage: false,
      hasPreviousPage: false,
    };
  }
  const itemsRaw = d.items ?? d.Items ?? [];
  const items = Array.isArray(itemsRaw)
    ? itemsRaw.map((r) => ({
        userId: String(r.userId ?? r.UserId ?? ""),
        employeeName: String(r.employeeName ?? r.EmployeeName ?? "—"),
        email: String(r.email ?? r.Email ?? ""),
        designation: String(r.designation ?? r.Designation ?? ""),
        departmentName: String(
          r.departmentName ?? r.DepartmentName ?? r.department ?? r.Department ?? ""
        ).trim(),
        officeName: String(r.officeName ?? r.OfficeName ?? r.office ?? r.Office ?? "").trim(),
        profileImageUrl: String(r.profileImageUrl ?? r.ProfileImageUrl ?? "").trim(),
        projects: String(r.projects ?? r.Projects ?? "").trim(),
        billableHours: Number(r.billableHours ?? r.BillableHours ?? 0) || 0,
        nonBillableHours: Number(r.nonBillableHours ?? r.NonBillableHours ?? 0) || 0,
        totalHours: Number(r.totalHours ?? r.TotalHours ?? 0) || 0,
        aggregateTimesheetStatus: String(r.aggregateTimesheetStatus ?? r.AggregateTimesheetStatus ?? "").trim(),
        timesheetCount: Number(r.timesheetCount ?? r.TimesheetCount ?? 0) || 0,
      }))
    : [];
  return {
    periodStart: d.periodStart ?? d.PeriodStart ?? null,
    periodEnd: d.periodEnd ?? d.PeriodEnd ?? null,
    items,
    totalCount: Number(d.totalCount ?? d.TotalCount ?? 0) || 0,
    page: Number(d.page ?? d.Page ?? 1) || 1,
    take: Number(d.take ?? d.Take ?? 20) || 20,
    totalPages: Number(d.totalPages ?? d.TotalPages ?? 0) || 0,
    hasNextPage: Boolean(d.hasNextPage ?? d.HasNextPage),
    hasPreviousPage: Boolean(d.hasPreviousPage ?? d.HasPreviousPage),
  };
};

/**
 * GET UserTimeSheet/EmployeePerformanceList — admin matrix grid (paginated).
 * @param {{ period: { periodStart?: Date, periodEnd?: Date, year?: number, month?: number }, page?: number, take?: number, search?: string, sortBy?: string, sortDescending?: boolean }} opts
 */
export const fetchEmployeePerformanceList = async (opts) => {
  const { period, page = 1, take = 20, search, sortBy, sortDescending = true } = opts || {};
  const params = {
    ...buildUserTimeSheetReportPeriodParams(period),
    page,
    take,
    sortDescending: Boolean(sortDescending),
  };
  const q = String(search ?? "").trim();
  if (q) params.search = q;
  if (sortBy != null && String(sortBy).trim() !== "") params.sortBy = String(sortBy).trim();
  const res = await axiosBasic.get(UserTimeSheet.EmployeePerformanceList, { params: omitEmptyParams(params) });
  assertTimesheetResponseOk(res.data);
  return normalizeEmployeePerformanceListPaged(res.data);
};

export const handleTimesheetApiError = (error, fallbackMessage) => {
  const msg =
    error?.response?.data?.message ??
    error?.response?.data?.Message ??
    (typeof error?.response?.data === "string" ? error.response.data : null) ??
    error?.message ??
    fallbackMessage;
  if (typeof posthog !== "undefined" && posthog.capture) {
    posthog.capture("timesheetApiError", {
      type: "exception",
      status: error?.response?.status,
      message: msg,
    });
  }
  Common.showErrorMessage(msg);
};
